import React, { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { MaintenancePage } from './MaintenancePage';
import { getMaintenanceStatus, type MaintenanceState } from './maintenanceClient';

const defaultPollIntervalMs = 30000;

type MaintenanceGateProps = PropsWithChildren<{
  initialState: MaintenanceState;
  maintenanceUrl?: string;
  onMaintenanceEnded?: () => void;
  pollIntervalMs?: number;
}>;

export const MaintenanceGate: React.FC<MaintenanceGateProps> = ({
  children,
  initialState,
  maintenanceUrl,
  onMaintenanceEnded = reloadPage,
  pollIntervalMs = defaultPollIntervalMs,
}) => {
  const [maintenanceState, setMaintenanceState] = useState(initialState);
  const maintenanceStateRef = useRef(initialState);

  useEffect(() => {
    maintenanceStateRef.current = maintenanceState;
  }, [maintenanceState]);

  useEffect(() => {
    if (!maintenanceUrl) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;

    const scheduleNextPoll = () => {
      if (cancelled) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        void pollMaintenanceStatus();
      }, pollIntervalMs);
    };

    const pollMaintenanceStatus = async () => {
      const currentState = maintenanceStateRef.current;
      const nextState = await getMaintenanceStatus(maintenanceUrl);
      if (cancelled) {
        return;
      }

      if (!nextState) {
        scheduleNextPoll();
        return;
      }

      if (currentState.enabled && !nextState.enabled) {
        onMaintenanceEnded();
      }

      if (currentState.enabled !== nextState.enabled || currentState.endTime !== nextState.endTime) {
        maintenanceStateRef.current = nextState;
        setMaintenanceState(nextState);
      }

      scheduleNextPoll();
    };

    scheduleNextPoll();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [maintenanceUrl, onMaintenanceEnded, pollIntervalMs]);

  if (maintenanceState.enabled) {
    return <MaintenancePage endTime={maintenanceState.endTime} />;
  }

  return <>{children}</>;
};

function reloadPage() {
  window.location.reload();
}
